import { Link } from 'react-router-dom';

export type LayoutStyle = 'grid2x2' | 'listcard' | 'bigfeatured' | 'magazine' | 'swiperow' | 'spotlight' | 'duosplit' | 'staggered' | 'fullstack';

interface Product {
  id: string | number;
  name: string;
  price: number;
  originalPrice: number;
  image: string;
  category: string;
  badge: string;
}

interface CollectionGridProps {
  products: Product[];
  layout: LayoutStyle;
  animating: boolean;
  onAddToCart: (product: Product) => void;
}

const badgeColors: Record<string, string> = {
  BESTSELLER: 'bg-amber-500',
  'TOP SELLING': 'bg-rose-600',
  TRENDING: 'bg-red-500',
  NEW: 'bg-emerald-500',
  HOT: 'bg-red-600',
  POPULAR: 'bg-violet-500',
  SALE: 'bg-orange-500',
};

function DiscountBadge({ price, originalPrice }: { price: number; originalPrice: number }) {
  if (originalPrice <= price) return null;
  const pct = Math.round((1 - price / originalPrice) * 100);
  return <span className="text-emerald-400 text-[10px] font-bold">{pct}% off</span>;
}

// ── LAYOUT 1: Classic 2x2 Grid (current) ──────────────────────────────────
function Layout1Grid2x2({ products, onAddToCart }: { products: Product[]; onAddToCart: (p: Product) => void }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {products.map(product => (
        <Link
          key={product.id}
          to={`/category?cat=${encodeURIComponent(product.category)}`}
          className="group relative bg-gray-50 dark:bg-slate-800 rounded-2xl overflow-hidden border border-gray-100 dark:border-slate-700 block"
        >
          <div className="relative overflow-hidden" style={{ paddingBottom: '125%' }}>
            <img src={product.image} alt={product.name} className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
            <div className="absolute top-2.5 left-2.5">
              <span className={`${badgeColors[product.badge] ?? 'bg-gray-700'} text-white text-[10px] font-black px-2 py-0.5 rounded-full`}>{product.badge}</span>
            </div>
            <button onClick={e => { e.preventDefault(); onAddToCart(product); }} className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center text-gray-900 cursor-pointer">
              <i className="ri-add-line text-base"></i>
            </button>
            <div className="absolute bottom-0 left-0 right-0 p-3">
              <p className="text-white font-semibold text-sm leading-tight truncate">{product.name}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-white font-black text-sm">₹{product.price}</span>
                <span className="text-white/50 text-xs line-through">₹{product.originalPrice}</span>
                <span className="ml-auto"><DiscountBadge price={product.price} originalPrice={product.originalPrice} /></span>
              </div>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

// ── LAYOUT 2: Horizontal List Cards ──────────────────────────────────────
function Layout2ListCard({ products, onAddToCart }: { products: Product[]; onAddToCart: (p: Product) => void }) {
  return (
    <div className="space-y-3">
      {products.map(product => {
        const pct = product.originalPrice > product.price ? Math.round((1 - product.price / product.originalPrice) * 100) : 0;
        return (
          <Link key={product.id} to={`/category?cat=${encodeURIComponent(product.category)}`} className="flex gap-3 bg-white dark:bg-slate-800 rounded-2xl overflow-hidden border border-gray-100 dark:border-slate-700 group block">
            {/* Image */}
            <div className="relative shrink-0 overflow-hidden rounded-l-2xl" style={{ width: 110, height: 130 }}>
              <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute top-2 left-2">
                <span className={`${badgeColors[product.badge] ?? 'bg-gray-700'} text-white text-[9px] font-black px-1.5 py-0.5 rounded-full`}>{product.badge}</span>
              </div>
            </div>
            {/* Info */}
            <div className="flex-1 py-3 pr-3 flex flex-col justify-between min-w-0">
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-[10px] font-bold uppercase tracking-wider">{product.category}</p>
                <p className="text-gray-900 dark:text-white font-black text-sm leading-tight mt-0.5 line-clamp-2">{product.name}</p>
              </div>
              <div>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="text-gray-900 dark:text-white font-black text-base">₹{product.price}</span>
                  {product.originalPrice > product.price && <span className="text-gray-400 text-xs line-through">₹{product.originalPrice}</span>}
                  {pct > 0 && <span className="ml-auto text-[10px] font-black px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600">{pct}% off</span>}
                </div>
                <button onClick={e => { e.preventDefault(); onAddToCart(product); }} className="mt-2 w-full py-1.5 rounded-xl bg-gray-900 dark:bg-slate-600 text-white text-xs font-bold flex items-center justify-center gap-1 cursor-pointer">
                  <i className="ri-shopping-bag-line"></i> Add to Cart
                </button>
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

// ── LAYOUT 3: Big Featured (1 large + 2 small) ───────────────────────────
function Layout3BigFeatured({ products, onAddToCart }: { products: Product[]; onAddToCart: (p: Product) => void }) {
  const [first, ...rest] = products;
  if (!first) return null;
  return (
    <div className="space-y-3">
      {/* Big card */}
      <Link to={`/category?cat=${encodeURIComponent(first.category)}`} className="block relative rounded-2xl overflow-hidden group" style={{ height: 280 }}>
        <img src={first.image} alt={first.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        <div className="absolute top-3 left-3 flex gap-2">
          <span className={`${badgeColors[first.badge] ?? 'bg-gray-700'} text-white text-[10px] font-black px-2.5 py-1 rounded-full`}>{first.badge}</span>
          <span className="bg-black/40 text-white text-[10px] font-bold px-2.5 py-1 rounded-full backdrop-blur-sm">FEATURED</span>
        </div>
        <button onClick={e => { e.preventDefault(); onAddToCart(first); }} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center text-gray-900 cursor-pointer">
          <i className="ri-add-line text-lg"></i>
        </button>
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="text-white font-black text-lg leading-tight">{first.name}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-white font-black text-xl">₹{first.price}</span>
            {first.originalPrice > first.price && <span className="text-white/50 text-sm line-through">₹{first.originalPrice}</span>}
            <DiscountBadge price={first.price} originalPrice={first.originalPrice} />
          </div>
        </div>
      </Link>
      {/* Small cards row */}
      <div className="grid grid-cols-2 gap-3">
        {rest.slice(0, 4).map(product => (
          <Link key={product.id} to={`/category?cat=${encodeURIComponent(product.category)}`} className="block relative rounded-2xl overflow-hidden group" style={{ height: 160 }}>
            <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent" />
            <div className="absolute top-2 left-2">
              <span className={`${badgeColors[product.badge] ?? 'bg-gray-700'} text-white text-[9px] font-black px-1.5 py-0.5 rounded-full`}>{product.badge}</span>
            </div>
            <button onClick={e => { e.preventDefault(); onAddToCart(product); }} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 flex items-center justify-center text-gray-900 cursor-pointer">
              <i className="ri-add-line text-sm"></i>
            </button>
            <div className="absolute bottom-0 left-0 right-0 p-2.5">
              <p className="text-white font-bold text-xs leading-tight truncate">{product.name}</p>
              <div className="flex items-center gap-1 mt-0.5">
                <span className="text-white font-black text-sm">₹{product.price}</span>
                <DiscountBadge price={product.price} originalPrice={product.originalPrice} />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ── LAYOUT 4: Magazine Style (asymmetric) ────────────────────────────────
function Layout4Magazine({ products, onAddToCart }: { products: Product[]; onAddToCart: (p: Product) => void }) {
  const [p1, p2, p3, p4] = products;
  return (
    <div className="space-y-3">
      {/* Row 1: 1 tall left + 2 stacked right */}
      <div className="flex gap-3" style={{ height: 260 }}>
        {p1 && (
          <Link to={`/category?cat=${encodeURIComponent(p1.category)}`} className="relative flex-1 rounded-2xl overflow-hidden group block">
            <img src={p1.image} alt={p1.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
            <div className="absolute top-2.5 left-2.5">
              <span className={`${badgeColors[p1.badge] ?? 'bg-gray-700'} text-white text-[10px] font-black px-2 py-0.5 rounded-full`}>{p1.badge}</span>
            </div>
            <button onClick={e => { e.preventDefault(); onAddToCart(p1); }} className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center text-gray-900 cursor-pointer">
              <i className="ri-add-line"></i>
            </button>
            <div className="absolute bottom-0 left-0 right-0 p-3">
              <p className="text-white font-black text-sm leading-tight">{p1.name}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-white font-black">₹{p1.price}</span>
                <DiscountBadge price={p1.price} originalPrice={p1.originalPrice} />
              </div>
            </div>
          </Link>
        )}
        <div className="flex flex-col gap-3" style={{ width: '45%' }}>
          {[p2, p3].filter(Boolean).map(product => product && (
            <Link key={product.id} to={`/category?cat=${encodeURIComponent(product.category)}`} className="relative flex-1 rounded-2xl overflow-hidden group block">
              <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <div className="absolute top-2 left-2">
                <span className={`${badgeColors[product.badge] ?? 'bg-gray-700'} text-white text-[9px] font-black px-1.5 py-0.5 rounded-full`}>{product.badge}</span>
              </div>
              <button onClick={e => { e.preventDefault(); onAddToCart(product); }} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 flex items-center justify-center text-gray-900 cursor-pointer">
                <i className="ri-add-line text-sm"></i>
              </button>
              <div className="absolute bottom-0 left-0 right-0 p-2">
                <p className="text-white font-bold text-xs truncate">{product.name}</p>
                <span className="text-white font-black text-sm">₹{product.price}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
      {/* Row 2: wide card */}
      {p4 && (
        <Link to={`/category?cat=${encodeURIComponent(p4.category)}`} className="relative block rounded-2xl overflow-hidden group" style={{ height: 130 }}>
          <img src={p4.image} alt={p4.name} className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
          <div className="absolute top-3 left-3">
            <span className={`${badgeColors[p4.badge] ?? 'bg-gray-700'} text-white text-[10px] font-black px-2 py-0.5 rounded-full`}>{p4.badge}</span>
          </div>
          <button onClick={e => { e.preventDefault(); onAddToCart(p4); }} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center text-gray-900 cursor-pointer">
            <i className="ri-add-line"></i>
          </button>
          <div className="absolute bottom-0 left-0 p-3">
            <p className="text-white font-black text-base">{p4.name}</p>
            <div className="flex items-center gap-2">
              <span className="text-white font-black text-lg">₹{p4.price}</span>
              {p4.originalPrice > p4.price && <span className="text-white/50 text-sm line-through">₹{p4.originalPrice}</span>}
              <DiscountBadge price={p4.price} originalPrice={p4.originalPrice} />
            </div>
          </div>
        </Link>
      )}
    </div>
  );
}

// ── LAYOUT 5: Swipe Row (horizontal scroll cards) ────────────────────────
function Layout5SwipeRow({ products, onAddToCart }: { products: Product[]; onAddToCart: (p: Product) => void }) {
  return (
    <div>
      <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
        {products.map(product => {
          const pct = product.originalPrice > product.price ? Math.round((1 - product.price / product.originalPrice) * 100) : 0;
          return (
            <Link
              key={product.id}
              to={`/category?cat=${encodeURIComponent(product.category)}`}
              className="shrink-0 block rounded-2xl overflow-hidden bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 group"
              style={{ width: 160 }}
            >
              <div className="relative overflow-hidden" style={{ height: 200 }}>
                <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                <div className="absolute top-2.5 left-2.5">
                  <span className={`${badgeColors[product.badge] ?? 'bg-gray-700'} text-white text-[9px] font-black px-2 py-0.5 rounded-full`}>{product.badge}</span>
                </div>
                <button onClick={e => { e.preventDefault(); onAddToCart(product); }} className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-white/90 flex items-center justify-center text-gray-900 cursor-pointer">
                  <i className="ri-add-line text-sm"></i>
                </button>
              </div>
              <div className="p-2.5">
                <p className="text-gray-900 dark:text-white font-black text-xs leading-tight line-clamp-1">{product.name}</p>
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-gray-900 dark:text-white font-black text-sm">₹{product.price}</span>
                  {product.originalPrice > product.price && <span className="text-gray-400 text-[10px] line-through">₹{product.originalPrice}</span>}
                </div>
                {pct > 0 && (
                  <div className="mt-1.5 w-full py-1 rounded-lg bg-emerald-50 text-emerald-600 text-[10px] font-black text-center">{pct}% OFF</div>
                )}
              </div>
            </Link>
          );
        })}
      </div>
      <p className="text-center text-gray-400 text-[10px] mt-1 flex items-center justify-center gap-1">
        <i className="ri-arrow-left-right-line"></i> Swipe to see more
      </p>
    </div>
  );
}

// ── LAYOUT 6: Spotlight (1 full-width hero + horizontal scroll below) ────
function Layout6Spotlight({ products, onAddToCart }: { products: Product[]; onAddToCart: (p: Product) => void }) {
  const [hero, ...rest] = products;
  if (!hero) return null;
  return (
    <div className="space-y-3">
      {/* Full-width hero */}
      <Link to={`/category?cat=${encodeURIComponent(hero.category)}`} className="block relative rounded-2xl overflow-hidden group" style={{ height: 320 }}>
        <img src={hero.image} alt={hero.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
        <div className="absolute top-4 left-4 flex gap-2 items-center">
          <span className={`${badgeColors[hero.badge] ?? 'bg-gray-700'} text-white text-[11px] font-black px-3 py-1 rounded-full`}>{hero.badge}</span>
          <span className="bg-white/20 backdrop-blur-sm text-white text-[11px] font-bold px-3 py-1 rounded-full border border-white/30">SPOTLIGHT</span>
        </div>
        <button onClick={e => { e.preventDefault(); onAddToCart(hero); }} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white flex items-center justify-center text-gray-900 cursor-pointer">
          <i className="ri-shopping-bag-line text-base"></i>
        </button>
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <p className="text-white/70 text-xs font-bold uppercase tracking-widest mb-1">{hero.category}</p>
          <p className="text-white font-black text-2xl leading-tight">{hero.name}</p>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-white font-black text-2xl">₹{hero.price}</span>
            {hero.originalPrice > hero.price && <span className="text-white/50 text-base line-through">₹{hero.originalPrice}</span>}
            <DiscountBadge price={hero.price} originalPrice={hero.originalPrice} />
          </div>
        </div>
      </Link>
      {/* Horizontal scroll strip */}
      <div className="flex gap-2.5 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
        {rest.slice(0, 5).map(product => (
          <Link key={product.id} to={`/category?cat=${encodeURIComponent(product.category)}`} className="shrink-0 block rounded-xl overflow-hidden group" style={{ width: 120, height: 150 }}>
            <div className="relative w-full h-full">
              <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <div className="absolute top-1.5 left-1.5">
                <span className={`${badgeColors[product.badge] ?? 'bg-gray-700'} text-white text-[8px] font-black px-1.5 py-0.5 rounded-full`}>{product.badge}</span>
              </div>
              <button onClick={e => { e.preventDefault(); onAddToCart(product); }} className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-white/90 flex items-center justify-center text-gray-900 cursor-pointer">
                <i className="ri-add-line text-xs"></i>
              </button>
              <div className="absolute bottom-0 left-0 right-0 p-2">
                <p className="text-white font-bold text-[10px] leading-tight truncate">{product.name}</p>
                <span className="text-white font-black text-xs">₹{product.price}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ── LAYOUT 7: Duo Split (2 tall side-by-side + 3 small below) ────────────
function Layout7DuoSplit({ products, onAddToCart }: { products: Product[]; onAddToCart: (p: Product) => void }) {
  const [p1, p2, p3, p4, p5] = products;
  return (
    <div className="space-y-3">
      {/* Two tall cards */}
      <div className="grid grid-cols-2 gap-3" style={{ height: 280 }}>
        {[p1, p2].filter(Boolean).map(product => product && (
          <Link key={product.id} to={`/category?cat=${encodeURIComponent(product.category)}`} className="relative rounded-2xl overflow-hidden group block">
            <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
            <div className="absolute top-3 left-3">
              <span className={`${badgeColors[product.badge] ?? 'bg-gray-700'} text-white text-[10px] font-black px-2 py-0.5 rounded-full`}>{product.badge}</span>
            </div>
            <button onClick={e => { e.preventDefault(); onAddToCart(product); }} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center text-gray-900 cursor-pointer">
              <i className="ri-add-line"></i>
            </button>
            <div className="absolute bottom-0 left-0 right-0 p-3">
              <p className="text-white/70 text-[9px] font-bold uppercase tracking-wider">{product.category}</p>
              <p className="text-white font-black text-sm leading-tight mt-0.5">{product.name}</p>
              <div className="flex items-center gap-1.5 mt-1">
                <span className="text-white font-black text-base">₹{product.price}</span>
                <DiscountBadge price={product.price} originalPrice={product.originalPrice} />
              </div>
            </div>
          </Link>
        ))}
      </div>
      {/* Three small cards */}
      <div className="grid grid-cols-3 gap-2">
        {[p3, p4, p5].filter(Boolean).map(product => product && (
          <Link key={product.id} to={`/category?cat=${encodeURIComponent(product.category)}`} className="relative rounded-xl overflow-hidden group block" style={{ height: 130 }}>
            <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            <div className="absolute top-1.5 left-1.5">
              <span className={`${badgeColors[product.badge] ?? 'bg-gray-700'} text-white text-[8px] font-black px-1.5 py-0.5 rounded-full`}>{product.badge}</span>
            </div>
            <button onClick={e => { e.preventDefault(); onAddToCart(product); }} className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-white/90 flex items-center justify-center text-gray-900 cursor-pointer">
              <i className="ri-add-line text-xs"></i>
            </button>
            <div className="absolute bottom-0 left-0 right-0 p-2">
              <p className="text-white font-bold text-[10px] truncate">{product.name}</p>
              <span className="text-white font-black text-xs">₹{product.price}</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ── LAYOUT 8: Staggered Tiles (Pinterest-style unequal heights) ──────────
function Layout8Staggered({ products, onAddToCart }: { products: Product[]; onAddToCart: (p: Product) => void }) {
  const heights = [220, 160, 180, 240, 150, 200];
  const leftProducts = products.filter((_, i) => i % 2 === 0);
  const rightProducts = products.filter((_, i) => i % 2 !== 0);
  const leftHeights = [220, 180, 150];
  const rightHeights = [160, 240, 200];

  const renderCard = (product: Product, height: number) => (
    <Link key={product.id} to={`/category?cat=${encodeURIComponent(product.category)}`} className="block relative rounded-2xl overflow-hidden group mb-3" style={{ height }}>
      <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
      <div className="absolute top-2.5 left-2.5">
        <span className={`${badgeColors[product.badge] ?? 'bg-gray-700'} text-white text-[9px] font-black px-2 py-0.5 rounded-full`}>{product.badge}</span>
      </div>
      <button onClick={e => { e.preventDefault(); onAddToCart(product); }} className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-white/90 flex items-center justify-center text-gray-900 cursor-pointer">
        <i className="ri-add-line text-sm"></i>
      </button>
      <div className="absolute bottom-0 left-0 right-0 p-3">
        <p className="text-white font-black text-xs leading-tight truncate">{product.name}</p>
        <div className="flex items-center gap-1 mt-0.5">
          <span className="text-white font-black text-sm">₹{product.price}</span>
          <DiscountBadge price={product.price} originalPrice={product.originalPrice} />
        </div>
      </div>
    </Link>
  );

  return (
    <div className="flex gap-3">
      <div className="flex-1">
        {leftProducts.slice(0, 3).map((p, i) => renderCard(p, leftHeights[i] ?? 180))}
      </div>
      <div className="flex-1 pt-5">
        {rightProducts.slice(0, 3).map((p, i) => renderCard(p, rightHeights[i] ?? 180))}
      </div>
    </div>
  );
}

// ── LAYOUT 9: Full Width Stack (bold full-width cards stacked) ────────────
function Layout9FullStack({ products, onAddToCart }: { products: Product[]; onAddToCart: (p: Product) => void }) {
  return (
    <div className="space-y-2.5">
      {products.slice(0, 4).map((product, idx) => {
        const pct = product.originalPrice > product.price ? Math.round((1 - product.price / product.originalPrice) * 100) : 0;
        const isLarge = idx === 0;
        return (
          <Link
            key={product.id}
            to={`/category?cat=${encodeURIComponent(product.category)}`}
            className="block relative rounded-2xl overflow-hidden group"
            style={{ height: isLarge ? 220 : 140 }}
          >
            <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
            <div className={`absolute inset-0 ${isLarge ? 'bg-gradient-to-r from-black/80 via-black/40 to-transparent' : 'bg-gradient-to-r from-black/75 via-black/30 to-transparent'}`} />
            {/* Left content */}
            <div className="absolute inset-0 flex flex-col justify-center p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className={`${badgeColors[product.badge] ?? 'bg-gray-700'} text-white text-[10px] font-black px-2.5 py-0.5 rounded-full`}>{product.badge}</span>
                <span className="text-white/60 text-[10px] font-bold uppercase tracking-wider">{product.category}</span>
              </div>
              <p className={`text-white font-black leading-tight ${isLarge ? 'text-xl' : 'text-base'}`}>{product.name}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className={`text-white font-black ${isLarge ? 'text-2xl' : 'text-lg'}`}>₹{product.price}</span>
                {product.originalPrice > product.price && <span className="text-white/50 text-sm line-through">₹{product.originalPrice}</span>}
                {pct > 0 && <span className="bg-emerald-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full">{pct}% OFF</span>}
              </div>
            </div>
            {/* Add button */}
            <button
              onClick={e => { e.preventDefault(); onAddToCart(product); }}
              className={`absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white flex items-center justify-center text-gray-900 cursor-pointer ${isLarge ? 'w-11 h-11' : 'w-9 h-9'}`}
            >
              <i className={`ri-shopping-bag-line ${isLarge ? 'text-base' : 'text-sm'}`}></i>
            </button>
          </Link>
        );
      })}
    </div>
  );
}

// ── MAIN EXPORT ──────────────────────────────────────────────────────────
export default function CollectionGrid({ products, layout, animating, onAddToCart }: CollectionGridProps) {
  const cls = `transition-all duration-200 ${animating ? 'opacity-0 scale-[0.98]' : 'opacity-100 scale-100'}`;

  return (
    <div className={cls}>
      {layout === 'grid2x2' && <Layout1Grid2x2 products={products} onAddToCart={onAddToCart} />}
      {layout === 'listcard' && <Layout2ListCard products={products} onAddToCart={onAddToCart} />}
      {layout === 'bigfeatured' && <Layout3BigFeatured products={products} onAddToCart={onAddToCart} />}
      {layout === 'magazine' && <Layout4Magazine products={products} onAddToCart={onAddToCart} />}
      {layout === 'swiperow' && <Layout5SwipeRow products={products} onAddToCart={onAddToCart} />}
      {layout === 'spotlight' && <Layout6Spotlight products={products} onAddToCart={onAddToCart} />}
      {layout === 'duosplit' && <Layout7DuoSplit products={products} onAddToCart={onAddToCart} />}
      {layout === 'staggered' && <Layout8Staggered products={products} onAddToCart={onAddToCart} />}
      {layout === 'fullstack' && <Layout9FullStack products={products} onAddToCart={onAddToCart} />}
    </div>
  );
}
